import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, Search, CheckCircle, XCircle, Crown, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GrantsTab({ language }) {
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [premiumPlan, setPremiumPlan] = useState('basic');
  const [catalogIds, setCatalogIds] = useState('');
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editRequests, setEditRequests] = useState([]);
  const [userGrants, setUserGrants] = useState(null);

  useEffect(() => {
    fetchEditRequests();
  }, []);

  const fetchEditRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/shop-edit-requests`);
      setEditRequests(data);
    } catch (e) {}
  };

  const fetchUserGrants = async (uid) => {
    try {
      const { data } = await axios.get(`${API}/admin/user-grants/${uid}`);
      setUserGrants(data);
    } catch (e) { setUserGrants(null); }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) return;
    try {
      const { data } = await axios.get(`${API}/admin/users?search=${searchQuery}&limit=10`);
      setUsers(data.users || []);
    } catch (e) {}
  };

  const selectUser = (u) => {
    setUserId(u._id);
    setUserName(u.name);
    setUsers([]);
    setSearchQuery('');
    fetchUserGrants(u._id);
  };

  const grantPremium = async () => {
    if (!userId) return;
    try {
      const { data } = await axios.post(`${API}/admin/grant-premium`, { user_id: userId, plan_id: premiumPlan });
      setMessage(data.message);
      fetchUserGrants(userId);
      setTimeout(() => setMessage(''), 3000);
    } catch (e) { setMessage(e.response?.data?.detail || 'Kļūda'); }
  };

  const grantCatalog = async () => {
    if (!userId || !catalogIds.trim()) return;
    const cats = catalogIds.split(',').map(c => c.trim()).filter(Boolean);
    try {
      const { data } = await axios.post(`${API}/admin/grant-catalog`, { user_id: userId, catalogs: cats });
      setMessage(data.message);
      setCatalogIds('');
      fetchUserGrants(userId);
      setTimeout(() => setMessage(''), 3000);
    } catch (e) { setMessage(e.response?.data?.detail || 'Kļūda'); }
  };

  const revokePremium = async () => {
    if (!userId) return;
    if (!window.confirm(language === 'lv' ? 'Atcelt Premium šim lietotājam?' : 'Revoke premium for this user?')) return;
    try {
      await axios.delete(`${API}/admin/revoke-premium/${userId}`);
      setMessage(language === 'lv' ? 'Premium atcelts' : 'Premium revoked');
      fetchUserGrants(userId);
      setTimeout(() => setMessage(''), 3000);
    } catch (e) { setMessage(e.response?.data?.detail || 'Kļūda'); }
  };

  const processEditRequest = async (reqId, action) => {
    try {
      await axios.put(`${API}/admin/shop-edit-requests/${reqId}`, { action });
      fetchEditRequests();
    } catch (e) { console.error(e); }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString(language === 'lv' ? 'lv-LV' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="space-y-6" data-testid="grants-tab">
      {message && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">{message}</div>
      )}

      {/* User Search */}
      <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
        <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#D97736]" />
          {language === 'lv' ? 'Izvēlieties lietotāju' : 'Select User'}
        </h3>
        <div className="flex gap-2 mb-3">
          <Input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchUsers()}
            placeholder={language === 'lv' ? 'Meklēt pēc vārda vai e-pasta...' : 'Search by name or email...'}
            className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
            data-testid="grant-user-search"
          />
          <Button onClick={searchUsers} className="bg-[#D97736] text-white h-11">
            <Search className="w-4 h-4" />
          </Button>
        </div>
        {users.length > 0 && (
          <div className="border border-[#E5E4E0] rounded-xl divide-y divide-[#E5E4E0] max-h-40 overflow-y-auto">
            {users.map(u => (
              <button key={u._id} onClick={() => selectUser(u)} className="w-full text-left px-4 py-2.5 hover:bg-[#F3F2EE] flex items-center gap-3 text-sm">
                <span className="font-medium text-[#121212]">{u.name}</span>
                <span className="text-[#5C5C5C]">{u.email}</span>
              </button>
            ))}
          </div>
        )}
        {userId && (
          <div className="mt-3 p-3 bg-[#D97736]/10 rounded-xl flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#D97736]" />
            <span className="font-medium text-sm text-[#121212]">{userName}</span>
            <span className="text-xs text-[#5C5C5C]">ID: {userId.slice(-8)}</span>
          </div>
        )}
      </div>

      {/* Current grants panel */}
      {userId && userGrants && (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="user-current-grants">
          <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4">
            {language === 'lv' ? `Aktīvie piešķīrumi — ${userName}` : `Active grants — ${userName}`}
          </h3>

          {/* Premium */}
          <div className="mb-4">
            <p className="text-[10px] uppercase font-semibold tracking-wide text-[#5C5C5C] mb-2">
              {language === 'lv' ? 'Premium' : 'Premium'}
            </p>
            {userGrants.premium ? (
              <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex-wrap" data-testid="current-premium">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-semibold text-[#121212]">
                      {userGrants.premium.plan_label || userGrants.premium.plan_id}
                      {userGrants.premium.granted_by === 'admin' && (
                        <span className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-200 rounded px-1.5 py-0.5">admin</span>
                      )}
                    </p>
                    <p className="text-xs text-[#5C5C5C]">
                      {language === 'lv' ? 'Aktīvs no' : 'Active from'} {fmtDate(userGrants.premium.activated_at)}
                      {' → '}
                      <span className="font-medium">{fmtDate(userGrants.premium.expires_at)}</span>
                    </p>
                  </div>
                </div>
                <Button onClick={revokePremium} variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="revoke-premium-btn">
                  {language === 'lv' ? 'Atcelt' : 'Revoke'}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-[#5C5C5C] italic">
                {language === 'lv' ? 'Nav aktīva Premium plāna' : 'No active premium plan'}
              </p>
            )}
          </div>

          {/* Catalogs */}
          <div>
            <p className="text-[10px] uppercase font-semibold tracking-wide text-[#5C5C5C] mb-2">
              {language === 'lv' ? 'Pieslēgtie katalogi' : 'Connected catalogs'}
            </p>
            {userGrants.catalog_applications && userGrants.catalog_applications.length > 0 ? (
              <div className="space-y-2" data-testid="current-catalogs">
                {userGrants.catalog_applications.map((c) => (
                  <div key={c.id} className="p-3 bg-[#F3F2EE] rounded-xl">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex flex-wrap gap-1.5">
                        {(c.selected_catalogs || []).map((cat) => (
                          <span key={cat} className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E5E4E0] text-[#121212]">
                            {cat}
                          </span>
                        ))}
                        {(c.extra_catalogs || []).map((cat) => (
                          <span key={cat} className="text-xs px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-800">
                            +{cat}
                          </span>
                        ))}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#5C5C5C]">
                          {fmtDate(c.paid_at || c.created_at)} → <span className="font-medium text-[#121212]">{fmtDate(c.expires_at)}</span>
                        </p>
                        {c.granted_by === 'admin' && (
                          <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">admin</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5C5C5C] italic">
                {language === 'lv' ? 'Nav pieslēgtu katalogu' : 'No active catalogs'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Grant Premium */}
      {userId && (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
          <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#D97736]" />
            {language === 'lv' ? 'Piešķirt Premium' : 'Grant Premium'}
          </h3>
          <div className="flex gap-3">
            <select value={premiumPlan} onChange={e => setPremiumPlan(e.target.value)} className="h-11 px-3 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA]" data-testid="grant-premium-select">
              <option value="basic">Basic (€9/{language === 'lv' ? 'gadā' : 'yr'})</option>
              <option value="premium">Premium (€29/{language === 'lv' ? 'gadā' : 'yr'})</option>
              <option value="premium_max">Premium Max (€49/{language === 'lv' ? 'gadā' : 'yr'})</option>
              <option value="premium_ultra">Premium Ultra (€99/{language === 'lv' ? 'gadā' : 'yr'})</option>
            </select>
            <Button onClick={grantPremium} className="bg-[#4A6B53] hover:bg-[#3a5a43] text-white" data-testid="grant-premium-btn">
              {language === 'lv' ? 'Piešķirt' : 'Grant'}
            </Button>
          </div>
        </div>
      )}

      {/* Grant Catalog */}
      {userId && (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
          <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#D97736]" />
            {language === 'lv' ? 'Piešķirt katalogu' : 'Grant Catalog'}
          </h3>
          <div className="space-y-3">
            <Input
              value={catalogIds}
              onChange={e => setCatalogIds(e.target.value)}
              placeholder={language === 'lv' ? 'Katalogu ID (komats atdalīti): easter, spring_garden' : 'Catalog IDs (comma separated)'}
              className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
              data-testid="grant-catalog-input"
            />
            <Button onClick={grantCatalog} className="bg-[#4A6B53] hover:bg-[#3a5a43] text-white" data-testid="grant-catalog-btn">
              {language === 'lv' ? 'Piešķirt katalogu' : 'Grant Catalog'}
            </Button>
          </div>
        </div>
      )}

      {/* Pending Edit Requests */}
      {editRequests.length > 0 && (
        <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6">
          <h3 className="font-outfit font-semibold text-lg text-[#121212] mb-4">
            {language === 'lv' ? 'Gaidāmie veikalu labojumi' : 'Pending Shop Edits'}
          </h3>
          <div className="space-y-3">
            {editRequests.map(r => (
              <div key={r.id} className="p-4 border border-[#E5E4E0] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{r.user_name} — {r.shop_name}</span>
                  <span className="text-xs text-[#5C5C5C]">{new Date(r.created_at).toLocaleDateString('lv-LV')}</span>
                </div>
                <div className="text-xs text-[#5C5C5C] mb-3">
                  {Object.entries(r.changes || {}).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {v}</div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => processEditRequest(r.id, 'approve')} className="bg-green-600 text-white text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" /> {language === 'lv' ? 'Apstiprināt' : 'Approve'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => processEditRequest(r.id, 'reject')} className="border-red-400 text-red-500 text-xs">
                    <XCircle className="w-3 h-3 mr-1" /> {language === 'lv' ? 'Noraidīt' : 'Reject'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GrantsTab;
